import { useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { VStack } from '@chakra-ui/react'
import { Content, EditorContent, useEditor } from '@tiptap/react'

import { ErrorCard } from '~components/ErrorCard'

import { CharacterCountView } from './CharacterCountView'
import { DEFAULT_MAX_CHAR_COUNT } from './constants'
import { RichTextMenu } from './RichTextMenu'
import { styles } from './styles'
import { getCharacterCountFromEditor, getRteExtensions } from './utils'

type RichTextEditorProps = {
  content: Content
  setContent?: (content: string) => void
  isReadOnly?: boolean
  onChange?: VoidFunction
  characterLimit?: number
}

// ProseMirror can throw on crafted HTML (GTA-123-007), contain the failure to
// the editor instead of taking down the whole page
export const RichTextEditor = (props: RichTextEditorProps) => (
  <ErrorBoundary fallback={<ErrorCard variant="inline" />}>
    <RichTextEditorInner {...props} />
  </ErrorBoundary>
)

const RichTextEditorInner = ({
  content,
  setContent,
  isReadOnly = false,
  onChange,
  characterLimit = DEFAULT_MAX_CHAR_COUNT,
}: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: getRteExtensions({ characterLimit }),
    onUpdate: ({ editor }) => {
      const numChars = getCharacterCountFromEditor(editor)

      setContent?.(numChars > 0 ? editor.getHTML() : '')
      onChange?.()
    },
    content,
    onFocus: () => {
      setIsFocused(true)
    },
    onBlur: () => {
      setIsFocused(false)
    },
  })

  if (isReadOnly) {
    editor?.setEditable(false)
  }

  const [isFocused, setIsFocused] = useState(false)

  return (
    <VStack
      width="full"
      wordBreak="break-word"
      alignItems="left"
      sx={styles({
        isFocused,
        isReadOnly,
      })}
      spacing={0}
    >
      {!isReadOnly && <RichTextMenu editor={editor} />}
      <EditorContent editor={editor} />
      {!!editor && !isReadOnly && (
        <CharacterCountView
          editor={editor}
          characterLimit={characterLimit}
          pt={3}
        />
      )}
    </VStack>
  )
}
